import type { FlagManager, FlagSchedule as Schedule } from '@/state/flags/useFlag';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { useScheduleEditor } from '@/state/flags/useScheduleEditor';
import { FlagSection, FIELD_CLASS, SaveStrip } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagScheduleProps = { manager: FlagManager; schedule: Schedule };

const COPY = FLAG_COPY.schedule;

/**
 * A scheduled publish and a scheduled turn-off (PUT /flags/:id/schedule). Crons Scheduler acts
 * on both (crons/scheduler/flag-plans).
 */
export const FlagSchedule = ({ manager, schedule }: FlagScheduleProps) => {
  const editor = useScheduleEditor({ manager, schedule, copy: COPY });

  return (
    <FlagSection
      title={COPY.title}
      help={COPY.help}
      description={COPY.description}
      tourAnchor="flag-schedule"
      footer={
        editor.isDirty ? (
          <SaveStrip
            blockingReason={editor.blockingReason}
            saveLabel={COPY.save}
            discardLabel={COPY.discard}
            onSave={editor.save}
            onDiscard={editor.discard}
            isSaving={editor.isSaving}
          />
        ) : undefined
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {editor.showsPublishAt && (
          <TimeField
            label={COPY.publishAt}
            value={editor.publishText}
            onChange={editor.setPublishText}
            canClear={editor.canClearPublish}
            onClear={editor.clearPublish}
            disabled={editor.isLocked}
          />
        )}
        <TimeField
          label={COPY.offAt}
          value={editor.offText}
          onChange={editor.setOffText}
          canClear={editor.canClearOff}
          onClear={editor.clearOff}
          disabled={editor.isLocked}
        />
      </div>
    </FlagSection>
  );
};

type TimeFieldProps = {
  label: string;
  value: string;
  onChange: (text: string) => void;
  canClear: boolean;
  onClear: () => void;
  disabled: boolean;
};

/**
 * A datetime-local with its own Clear. The input reports '' while a date is half typed, so
 * clearing a schedule is this button's job, never an empty value's.
 */
const TimeField = ({ label, value, onChange, canClear, onClear, disabled }: TimeFieldProps) => (
  <div className="grid gap-1.5">
    <div className="flex min-h-5 items-center justify-between gap-2">
      <span className="micro-label">{label}</span>
      {canClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label={`${COPY.clear} ${label}`}
          className="text-dim hover:text-foreground focus-visible:ring-ring rounded text-micro focus-visible:ring-2 focus-visible:outline-none"
        >
          {COPY.clear}
        </button>
      )}
    </div>
    <Input
      type="datetime-local"
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
      disabled={disabled}
      aria-label={label}
      className={cn(FIELD_CLASS, 'font-mono text-meta')}
    />
  </div>
);
